import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { HiArrowLeft, HiLocationMarker, HiClock } from 'react-icons/hi';
import { IoRadioButtonOn } from 'react-icons/io5';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import './NCWatch.css';

export default function Watch({ config }) {
  const { gameId } = useParams();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => { loadGame(); }, [gameId]);

  useEffect(() => {
    const isLive = game?.status === 'live';
    const playbackUrl = isLive ? game?.playbackUrl : game?.recordingUrl;
    if (!playbackUrl || !videoRef.current) return;

    // Create a video-js element inside the container div — video.js owns this node,
    // React owns the container. This avoids conflicts when dispose() removes the element.
    const videoEl = document.createElement('video-js');
    videoEl.classList.add('vjs-big-play-centered');
    videoRef.current.appendChild(videoEl);

    const player = videojs(videoEl, {
      controls: true, autoplay: isLive ? 'muted' : false,
      preload: 'auto', fill: true, liveui: isLive,
      skipButtons: { forward: 5, backward: 5 },
    });
    player.src({ src: playbackUrl, type: 'application/x-mpegURL' });
    playerRef.current = player;

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [game]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGame = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/games/${gameId}`);
      const data = await res.json();
      setGame(data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="nc-watch-loading">
      <div className="nc-watch-spinner" />
    </div>
  );

  if (!game || (game.status !== 'live' && !game.recordingUrl)) return (
    <div className="nc-watch-unavailable">
      <p>{!game ? 'Game not found' : 'Stream not available'}</p>
      <Link to="/" className="nc-watch-back"><HiArrowLeft /> Back to Home</Link>
    </div>
  );

  const isLive = game.status === 'live';

  return (
    <div className="nc-watch">
      <div className="nc-watch-player-wrap">
        <div ref={videoRef} style={{ width: '100%', height: '100%' }} />
      </div>

      <div className="nc-watch-info">
        <Link to="/" className="nc-watch-back"><HiArrowLeft /> All Games</Link>

        <div className="nc-watch-badges">
          {isLive
            ? <span className="nc-watch-live"><IoRadioButtonOn /> LIVE</span>
            : <span className="nc-watch-recorded">REPLAY</span>
          }
          <span className="nc-watch-sport">{game.sport}</span>
        </div>

        <h1 className="nc-watch-title">{game.homeTeam} vs {game.awayTeam}</h1>

        <div className="nc-watch-meta">
          <span><HiLocationMarker /> {game.venue}{game.field ? ` — ${game.field}` : ''}</span>
          <span><HiClock /> {new Date(game.scheduledStart).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</span>
        </div>
      </div>
    </div>
  );
}
